// React
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import i18next from 'i18next';

// Components
import {
    TextInput,
    Dropdown,
    ToggleButton,
    PrimaryButton,
    SecondaryButton
} from '../../../msreact-kit/packages/html-components';
import Footer from '../../BaseLayout/footer';

// Actions  
import {
    createShippingRule,
    updateShippingRule,
    getShippingSacCodes
} from '../../../actions/shipping-action';
import { setSnackbar } from '../../../actions/generic-action';
import Loader from '../../Shared/Utils/Loader';
import CustomizedSnackbars from '../../Shared/Utils/SnackBar';

// Data
import {
    SHIPPING_TYPE_OPTIONS, SHIPMENT_MODE_OPTIONS, CURRENCY_OPTIONS,
    emptyForm, SHIPPING_RULE_MODE, REGION_CURRENCY_MAP
} from '../data';

// CSS
import './index.scss';

const requiredValidation = { rule: { required: { isRequired: true } } };

const DESC_MAX_LENGTH = 150;
const VALUE_MAX_LENGTH = 10;

const ShippingDetails = (props) => {
    const queryParams = new URLSearchParams(props.location?.search);
    const mode = queryParams.get('mode');
    const shippingRule = props.location?.state?.shippingRule;

    const dispatch = useDispatch();

    const { content, snackbarStatus } = useSelector((state) => ({
        content: state.ContentReducer.content,
        snackbarStatus: state.GenericReducer.snackbarStatus
    }));
    const shippingContent =
        content?.components?.shipping_management;
    const [form, setForm] = useState(shippingRule ? { ...shippingRule } : { ...emptyForm });
    const [errors, setErrors] = useState({});
    const [isActive, setIsActive] = useState(
        mode === SHIPPING_RULE_MODE.add ? true : shippingRule?.isActive
    );
    const [submitting, setSubmitting] = useState(false);
    const isEdit = mode === SHIPPING_RULE_MODE.edit;
    const [charCountEntered, setCharCountEntered] = useState(isEdit ? form?.shippingRuleName?.length || 0 : 0);
    const [sacCodeOptions, setSacCodeOptions] = useState([]);
    const [loadingSacCodes, setLoadingSacCodes] = useState(false);

    useEffect(() => {
        if (mode === SHIPPING_RULE_MODE.edit && form?.isActive) {
            setIsActive(form?.isActive);
        }
    }, [form?.isActive, mode]);
    
    useEffect(() => {        
        const currencyOpts = currencyOptions();
        if (currencyOpts.length === 1 && !form.currency) {
            setForm((prev) => ({ ...prev, currency: currencyOpts[0].value }));
        }
        if (SHIPPING_TYPE_OPTIONS.length === 1 && !form.shippingType) {
            setForm((prev) => ({ ...prev, shippingType: SHIPPING_TYPE_OPTIONS[0].value }));
        }
    }, [mode]);
    
    useEffect(() => {
        const fetchSacCodes = async () => {
            setLoadingSacCodes(true);
            const result = await dispatch(getShippingSacCodes());
            if (result && Array.isArray(result)) {
                const options = result.map(item => ({
                    label: `${item.code}`,
                    value: item.code
                }));
                setSacCodeOptions(options);
                
                if (options.length === 1 && !form.sacCode) {
                    setForm((prev) => ({ ...prev, sacCode: options[0].value }));
                }
            }
            setLoadingSacCodes(false);
        };

        fetchSacCodes();
    }, [dispatch]);
    
    const handleChange = (data) => {
        let val = data.value;
        let fieldError = data.error || '';

        if (data.name === 'shippingRuleName') {
            if (val.length > DESC_MAX_LENGTH) {
                setCharCountEntered(DESC_MAX_LENGTH);
                val = val.slice(0, DESC_MAX_LENGTH);
                fieldError = shippingContent?.shipping_details?.validation?.description_max_length;
            } else {
                setCharCountEntered(val.length);
            }

        }

        if (data.name === 'shippingCharge') {            
            if (/[^\d.]/.test(val)) {
                fieldError = shippingContent?.shipping_details?.validation?.value_numeric;
            }            
            val = val.replace(/[^\d.]/g, '');            
            const parts = val.split('.');
            if (parts.length > 2) val = parts[0] + '.' + parts.slice(1).join('');
            // Limit to 2 decimal places silently (no error)
            if (val.includes('.')) {
                const [integer, decimal] = val.split('.');
                val = integer + '.' + (decimal || '').slice(0, 2);
            }
            if (val.replace('.', '').length > VALUE_MAX_LENGTH) {
                val = val.slice(0, VALUE_MAX_LENGTH); 
                fieldError = shippingContent?.shipping_details?.validation?.value_max_length;
            }
        }

        setForm((prev) => ({ ...prev, [data.name]: val }));
        setErrors((prev) => ({ ...prev, [data.name]: fieldError }));
    };

    const handleBlur = (data) => {
        if (data.error) {
            setErrors((prev) => ({ ...prev, [data.name]: data.error }));
        }
    };

    const handleDropdownChange = (fieldName) => (data) => {
        const val = data.value?.value || data.value || '';
        setForm((prev) => ({ ...prev, [fieldName]: val }));
    };

    const handleBack = () => {
        props.history.push({
            pathname: '/dashboard/shipping-management',
            state: {
                successMessage: ''
            }
        });
    };

    const handleSubmit = async () => {
        const payload = {
            shippingRuleName: form.shippingRuleName,
            sacCode: form.sacCode,
            shippingType: form.shippingType,
            shipmentMode: form.shipmentMode,
            currency: form.currency,
            shippingCharge: form.shippingCharge,
            isActive: isActive
        };
        if (mode === SHIPPING_RULE_MODE.add) {

            setSubmitting(true);
            const result = await dispatch(createShippingRule(payload));
            setSubmitting(false);
            if (result) {
                props.history.push({
                    pathname: '/dashboard/shipping-management',
                    state: {
                        action: mode,
                        successMessage: shippingContent?.shipping_details?.messages?.shipping_rule_created
                    }
                });
            }
            return;
        }

        payload.shippingRuleId = shippingRule?.shippingRuleId;
        // Edit mode — call PUT API with new status
        setSubmitting(true);
        const result = await dispatch(updateShippingRule(shippingRule?.shippingRuleId, payload));
        setSubmitting(false);
        if (result) {
            props.history.push({
                pathname: '/dashboard/shipping-management',
                state: {
                    action: mode,
                    successMessage: shippingContent?.shipping_details?.messages?.shipping_rule_updated
                }
            });
        }
    };

    const getTitle = () => {
        if (mode === SHIPPING_RULE_MODE.add) return shippingContent?.shipping_details?.title_create;
        return shippingContent?.shipping_details?.title_edit;
    };

    const getDropdownValue = (options, fieldValue) =>
        options.find((o) => o.value === fieldValue) || null;
    
    const isFormValid = (() => {
        const mandatoryMet =
            form.shippingRuleName &&
            form.shippingCharge &&
            form.shippingType &&
            form.shipmentMode &&
            form.sacCode &&
            form.currency;
        if (!mandatoryMet) return false;        
        if (errors.name || errors.value) return false;
        return true;
    })();
    const currencyOptions = () => {
        return CURRENCY_OPTIONS
            .filter((item) => {
                const allowedCurrency = REGION_CURRENCY_MAP[process.env.REACT_APP_REGION];
                return allowedCurrency ? item.value === allowedCurrency : true; // If no region match, show all
            })
            .map((item) => ({
                label: item.label,
                value: item.value
            }));
    };
    
    const renderFormMode = () => (
        <div className='shipping-details__form'>
            <p className='shipping-details__form__section-title'>
                {isEdit ? `${shippingContent?.shipping_details?.edit} ${shippingRule?.shippingRuleName} ${shippingContent?.shipping_details?.settings}` : shippingContent?.shipping_details?.title_create}
            </p>

            {/* Row 1 — Shipping Rule Name */}
            <div className='shipping-details__form__row'>
                <div className='shipping-details__form__field'>
                    <TextInput
                        displayName={shippingContent?.shipping_details?.fields?.name}
                        name='shippingRuleName'
                        value={form.shippingRuleName}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.shippingRuleName}
                        validation={requiredValidation}
                    />
                    <div className='shipping-details__form__char'>
                        {i18next.t('DYNAMIC_FORM.SHARED.ERROR_MESSAGE.MAX_CHAR')} - {DESC_MAX_LENGTH - charCountEntered}
                    </div>

                </div>
                <div className='shipping-details__form__field'>
                    <Dropdown
                        displayName={shippingContent?.shipping_details?.fields?.sac_code}
                        name='sacCode'
                        options={sacCodeOptions}
                        value={getDropdownValue(sacCodeOptions, form.sacCode)}
                        handleChange={handleDropdownChange('sacCode')}
                        validation={requiredValidation}
                        disabled={loadingSacCodes}
                    />
                </div>
            </div>

            {/* Row 2 — Base Cost | Currency+Metrics */}
            <div className='shipping-details__form__row'>
                <div className='shipping-details__form__field'>
                    <TextInput
                        displayName={shippingContent?.shipping_details?.fields?.base_shipping_cost}
                        name='shippingCharge'
                        value={form.shippingCharge}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        error={errors.shippingCharge}
                        validation={requiredValidation}
                    />
                </div>
                <div className='shipping-details__form__field-group'>
                    {
                        <div className='shipping-details__form__field'>
                            <Dropdown
                                displayName={shippingContent?.shipping_details?.fields?.currency}
                                name='currency'
                                options={currencyOptions()}
                                value={getDropdownValue(currencyOptions(), form.currency)}
                                handleChange={handleDropdownChange('currency')}
                                validation={requiredValidation}
                            />
                        </div>
                    }
                    {
                        <div className='shipping-details__form__field'>
                            <Dropdown
                                displayName={shippingContent?.shipping_details?.fields?.metrics}
                                name='shippingType'
                                options={SHIPPING_TYPE_OPTIONS}
                                value={getDropdownValue(SHIPPING_TYPE_OPTIONS, form.shippingType)}
                                handleChange={handleDropdownChange('shippingType')}
                                validation={requiredValidation}
                            />
                        </div>
                    }
                </div>
            </div>

            {/* Row 3 — Delivery Mode | Status */}
            <div className='shipping-details__form__row'>
                <div className='shipping-details__form__field'>
                    <Dropdown
                        displayName={shippingContent?.shipping_details?.fields?.mode_of_delivery}
                        name='shipmentMode'
                        options={SHIPMENT_MODE_OPTIONS}
                        value={getDropdownValue(SHIPMENT_MODE_OPTIONS, form.shipmentMode)}
                        handleChange={handleDropdownChange('shipmentMode')}
                        validation={requiredValidation}
                    />
                </div>
                <div className='shipping-details__form__field'>
                    <div
                        className={`shipping-details__status-toggle${mode === SHIPPING_RULE_MODE.add ? ' shipping-details__status-toggle--disabled' : ''}`}
                    >
                        <span className='shipping-details__status-toggle__label'>
                            {shippingContent?.shipping_details?.fields?.status}
                        </span>
                        <div className='shipping-details__status-toggle__row'>
                            <ToggleButton
                                id='shipping-status-toggle'
                                name='status'
                                label=''
                                checked={isActive}
                                handleToggle={(val) => {
                                    if (mode === SHIPPING_RULE_MODE.edit) {
                                        setIsActive(val);
                                    }
                                }}
                                disabled={mode === SHIPPING_RULE_MODE.add}
                            />
                            <span className='shipping-details__status-toggle__state'>
                                {isActive
                                    ? shippingContent?.shipping_details?.status_labels?.enabled
                                    : shippingContent?.shipping_details?.status_labels?.disabled}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            {isEdit && (
                <p className='shipping-details__form__hint'>{shippingContent?.page?.edit_info}</p>
            )}
        </div>
    );

    const handleSnackBarClose = () => {
        dispatch(setSnackbar({ showSnackbar: false, message: '' }));
    };

    useEffect(() => {
        return () => {
            dispatch(setSnackbar({ showSnackbar: false, message: '' }));
        };
    }, []);

    return (
        <>
            {(submitting) && (
                <Loader />
            )}
            {snackbarStatus?.showSnackbar && (
                <CustomizedSnackbars
                    severity={snackbarStatus?.severity}
                    message={snackbarStatus?.message}
                    changeState={handleSnackBarClose}
                />
            )}
            <div className='shipping-details__container'>
                <div className='shipping-details__container__title-wrapper'>
                    <div className='shipping-details__container__title'>
                        <h5 className='shipping-details__container__header -size-l'>
                            {getTitle()}
                        </h5>
                        <p className='shipping-details__container__subtitle'>
                            {isEdit ? shippingContent?.shipping_details?.title_edit : shippingContent?.shipping_details?.add_section_title}
                        </p>
                    </div>
                </div>

                <div className='shipping-details__container__content'>
                    {renderFormMode()}

                    <div className='shipping-details__container__buttons'>
                        <SecondaryButton
                            label={shippingContent?.shipping_details?.buttons?.cancel}
                            onClick={handleBack}
                            name='back'
                        />
                        <PrimaryButton
                            label={
                                mode === SHIPPING_RULE_MODE.add
                                    ? shippingContent?.shipping_details?.buttons?.create
                                    : shippingContent?.shipping_details?.buttons?.update
                            }
                            onClick={handleSubmit}
                            name='submit'
                            disabled={!isFormValid}
                        />
                    </div>
                </div>
            </div>

            <Footer customClassName='d-lg-none' />
        </>
    );
};

ShippingDetails.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired
};

export default ShippingDetails;
